import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';

// 3rd party libraries
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

// Our Own imports
import { FaultCodes } from '@feedflo/fault-codes';
import { useCan, Privilege } from '@feedflo/access-control';
import Page from '../../atoms/Page';
import Heading from '../../atoms/Heading';
import Select from '../../atoms/Select';
import ComboboxMulti from '../../atoms/ComboboxMulti';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import StatusBadge from '../../atoms/StatusBadge';
import DeviceConnectivityModal from '../../molecules/DeviceConnectivityModal';
import FeedFloTable from '../../organisms/FeedFloTable';
import TransactionChart from '../../organisms/TransactionChart';
import {
  DEVICE_MONITOR_FILTER_OPTIONS_GQL,
  DEVICE_MONITOR_DEVICES_GQL,
  DEVICE_MONITOR_INTERNET_SCORES_GQL,
} from './queries';

// Relative path imports
import { computeInternetScore } from '../../utils/internetScore';
import './DeviceMonitorPage.scss';

const PAGE_SIZE = 50;

const SORT_OPTIONS = [
  { id: 'oldestUpload', name: 'Oldest Upload', value: 'oldestUpload' },
  { id: 'newestUpload', name: 'Newest Upload', value: 'newestUpload' },
  { id: 'orgAndBarn', name: 'Org + Barn Name', value: 'orgAndBarn' },
  { id: 'barnName', name: 'Barn Name', value: 'barnName' },
  { id: 'serial', name: 'Sensor Serial', value: 'serial' },
];

const STATUS_OPTIONS = [
  { id: 'all', name: 'All Devices', value: 'all' },
  { id: 'online', name: 'Online', value: 'online' },
  { id: 'offline', name: 'Offline', value: 'offline' },
];


function buildOrderBy(sortBy) {
  switch (sortBy) {
    case 'oldestUpload':
      return { most_recent_transaction_occured_at: 'asc_nulls_first' };
    case 'newestUpload':
      return { most_recent_transaction_occured_at: 'desc_nulls_last' };
    case 'orgAndBarn':
      return [{ org_name: 'asc' }, { barn_name: 'asc' }, { feedline_name: 'asc' }];
    case 'barnName':
      return [{ barn_name: 'asc' }, { feedline_name: 'asc' }];
    case 'serial':
    default:
      return [{ serial: 'asc' }];
  }
}

function DeviceMonitorPage({ viewportRef = null, titleSegments = [] }) {
  // --- State hooks ---
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [selectedBarnIds, setSelectedBarnIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newestUpload');
  const [offset, setOffset] = useState(0);
  const [allDevices, setAllDevices] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [internetScores, setInternetScores] = useState({});
  const [connectivityModalDevice, setConnectivityModalDevice] = useState(null);
  const [chartModalDevice, setChartModalDevice] = useState(null);

  // --- Atoms and other Hooks ---
  const { canAnywhere, loading: privilegesLoading } = useCan();
  const connectivityModalRef = useRef(null);
  const chartModalRef = useRef(null);

  // --- Apollo/GraphQL hooks ---
  const { data: filterOptionsData } = useQuery(DEVICE_MONITOR_FILTER_OPTIONS_GQL, {
    skip: privilegesLoading,
    context: { headers: { 'x-hasura-role': 'user' } },
  });

  const whereClause = useMemo(() => {
    const feedLineWhere = {};
    if (selectedBarnIds.length > 0) {
      feedLineWhere.farm_id = { _in: selectedBarnIds };
    }
    if (selectedOrgIds.length > 0) {
      feedLineWhere.farm = { organization_id: { _in: selectedOrgIds } };
    }

    const assignmentWhere = {
      ended_at: { _is_null: true },
      started_at: { _is_null: false },
    };
    if (Object.keys(feedLineWhere).length > 0) {
      assignmentWhere.feed_line = feedLineWhere;
    }

    const where = { device_assignments: assignmentWhere };
    if (statusFilter === 'online') {
      where._not = {
        faults: { ended_at: { _is_null: true }, code: { _eq: FaultCodes.DEVICE_UNREACHABLE } },
      };
    } else if (statusFilter === 'offline') {
      where.faults = {
        ended_at: { _is_null: true },
        code: { _eq: FaultCodes.DEVICE_UNREACHABLE },
      };
    }
    return where;
  }, [selectedOrgIds, selectedBarnIds, statusFilter]);

  const orderBy = useMemo(() => buildOrderBy(sortBy), [sortBy]);

  const last24hTimestamp = useMemo(() => dayjs.tz().subtract(24, 'hour').unix(), []);

  const { data: devicesData, loading: devicesLoading, error: devicesError } = useQuery(
    DEVICE_MONITOR_DEVICES_GQL,
    {
      variables: {
        limit: PAGE_SIZE,
        offset,
        where: whereClause,
        orderBy,
        deviceUnreachableFaultCode: FaultCodes.DEVICE_UNREACHABLE,
      },
      fetchPolicy: 'network-only',
      skip: privilegesLoading,
      context: { headers: { 'x-hasura-role': 'user' } },
      notifyOnNetworkStatusChange: true,
    },
  );

  const [fetchInternetScores] = useLazyQuery(DEVICE_MONITOR_INTERNET_SCORES_GQL, {
    fetchPolicy: 'network-only',
    context: { headers: { 'x-hasura-role': 'user' } },
  });


  // --- Navigation/side effects ---

  // Accumulate devices as pages load
  useEffect(() => {
    if (!devicesData?.device || devicesLoading) return;
    const newDevices = devicesData.device;
    if (offset === 0) {
      setAllDevices(newDevices);
    } else {
      setAllDevices((prev) => [...prev, ...newDevices]);
    }
    setHasMore(newDevices.length === PAGE_SIZE);
  }, [devicesData, devicesLoading]);

  // Reset when filters or sort change
  useEffect(() => {
    setOffset(0);
    setAllDevices([]);
    setHasMore(true);
    setInternetScores({});
  }, [selectedOrgIds, selectedBarnIds, statusFilter, sortBy]);

  // Fetch internet scores for any newly loaded devices
  useEffect(() => {
    if (allDevices.length === 0) {
      return;
    }
    const newIds = allDevices
      .filter((d) => !Object.prototype.hasOwnProperty.call(internetScores, d.id))
      .map((d) => d.id);
    if (newIds.length === 0) {
      return;
    }
    fetchInternetScores({
      variables: { deviceIds: newIds, last24h: last24hTimestamp },
      onCompleted: (data) => {
        setInternetScores((prev) => {
          const next = { ...prev };
          (data?.device || []).forEach((d) => {
            next[d.id] = d.device_data_aggregate?.aggregate;
          });
          return next;
        });
      },
      onError: (err) => {
        console.error('Internet scores fetch error:', err);
        Sentry.captureException(err);
      },
    });
  }, [allDevices]);

  // Infinite scroll — re-registers handler when pagination state changes
  useEffect(() => {
    if (!viewportRef?.current) return;
    const handler = () => {
      if (!viewportRef?.current) return;
      const { scrollHeight, scrollTop, clientHeight } = viewportRef.current;
      if (Math.abs(scrollHeight - scrollTop - clientHeight) < 1 && hasMore && !devicesLoading) {
        setOffset((prev) => prev + PAGE_SIZE);
      }
    };
    viewportRef.current.addEventListener('scroll', handler);
    return () => viewportRef?.current?.removeEventListener('scroll', handler);
  }, [viewportRef, hasMore, devicesLoading]);

  // Open/close the HTTP chart dialog
  useEffect(() => {
    if (chartModalDevice) {
      chartModalRef.current?.showModal();
    } else {
      chartModalRef.current?.close();
    }
  }, [chartModalDevice]);

  // --- Derived state ---
  const hasStaffAccess = canAnywhere('feature', Privilege.FEATURE_STAFF_ACCESS);

  const orgOptions = useMemo(
    () => (filterOptionsData?.organization || []).map((o) => ({ id: o.id, name: o.name })),
    [filterOptionsData],
  );

  const barnOptions = useMemo(
    () => (filterOptionsData?.farm || []).map((f) => ({ id: f.id, name: f.name })),
    [filterOptionsData],
  );

  const pageTitleSegments = useMemo(() => ['Device Monitor', ...titleSegments], [titleSegments]);

  const tableHeaders = [
    { t: 'ORG', s: 'm' },
    { t: 'FARM', s: 'm' },
    { t: 'FEED LINE', s: 'm' },
    { t: 'SERIAL', s: 'm' },
    { t: 'INTERNET SCORE', s: 'm' },
    { t: 'LAST UPLOAD', s: 'm' },
    { t: 'STATUS', s: 'm' },
    { t: 'ACTIONS', s: 's' },
  ];

  const tableRows = useMemo(() => {
    return allDevices.map((device) => {
      const feedLine = device?.device_assignments?.[0]?.feed_line;
      const farm = feedLine?.farm;
      const org = farm?.organization;
      const isOnline = device?.active_device_unreachable_fault?.length === 0;
      const lastUpload = device?.most_recent_transaction?.[0]?.occured_at;

      const orgLink = org ? <Link to={`/orgs/${org.id}`}>{org.name}</Link> : '—';
      const farmLink = farm ? <Link to={`/b/${farm.id}`}>{farm.name}</Link> : '—';
      const feedLineLink =
        farm && feedLine ? (
          <Link to={`/b/${farm.id}/line/${feedLine.id}`}>{feedLine.name}</Link>
        ) : (
          '—'
        );
      const serialLink = (
        <Link to={`/devices?d=${encodeURIComponent(device.serial)}&name=${encodeURIComponent(device.serial)}`}>
          {device.serial}
        </Link>
      );

      const scoreAggregate = internetScores[device.id];
      let internetScoreCell;
      if (!Object.prototype.hasOwnProperty.call(internetScores, device.id)) {
        internetScoreCell = <LoadingSkeleton />;
      } else {
        const scoreResult = computeInternetScore(scoreAggregate);
        const { grade, averageDelay, dataCoverage } = scoreResult ?? {};
        const scoreTooltip = scoreResult
          ? `Average Delay: ${averageDelay.toFixed(0)}s\nData Coverage: ${Math.min(100, (dataCoverage * 100)).toFixed(0)}%`
          : undefined;
        internetScoreCell = grade ? (
          <span
            className={`DeviceMonitorPage-scoreGrade DeviceMonitorPage-scoreGrade--${grade.replace('+', 'plus')}`}
            title={scoreTooltip}
          >
            {grade}
          </span>
        ) : (
          '—'
        );
      }

      const lastUploadCell = lastUpload ? dayjs.tz(1000 * lastUpload).fromNow() : '—';

      const statusCell = (
        <StatusBadge
          text={isOnline ? 'Online' : 'Offline'}
          icon={isOnline ? 'logo' : 'warning'}
          status={isOnline ? 'success' : 'error'}
        />
      );

      const actionsCell = (
        <div className="DeviceMonitorPage-actions">
          <button
            className="DeviceMonitorPage-actionButton"
            title="View Connectivity"
            onClick={() =>
              setConnectivityModalDevice({
                id: device.id,
                serial: device.serial,
                feedLineName: feedLine?.name || device.serial,
              })
            }
          >
            🔍
          </button>
          <button
            className="DeviceMonitorPage-actionButton"
            title="View HTTP Chart"
            onClick={() => setChartModalDevice({ id: device.id, serial: device.serial })}
          >
            📊
          </button>
        </div>
      );

      return [orgLink, farmLink, feedLineLink, serialLink, internetScoreCell, lastUploadCell, statusCell, actionsCell];
    });
  }, [allDevices, internetScores]);

  // --- Function definitions ---
  const handleOrgChange = useCallback((selectedItems) => {
    setSelectedOrgIds(selectedItems.map((item) => item.id));
  }, []);

  const handleBarnChange = useCallback((selectedItems) => {
    setSelectedBarnIds(selectedItems.map((item) => item.id));
  }, []);

  // --- Render ---
  if (!privilegesLoading && !hasStaffAccess) {
    return (
      <Page className="DeviceMonitorPage" titleSegments={pageTitleSegments}>
        <div className="DeviceMonitorPage-content">
          <div className="DeviceMonitorPage-header">
            <Heading level={3}>Access Denied</Heading>
          </div>
          <p>This page is only available to FeedFlo staff members.</p>
        </div>
      </Page>
    );
  }

  if (devicesError) {
    console.error('Device Monitor query error:', devicesError);
    Sentry.captureException(devicesError);
    toast.error('Error loading device data');
    return (
      <Page className="DeviceMonitorPage" titleSegments={pageTitleSegments}>
        <div className="DeviceMonitorPage-content">
          <div className="DeviceMonitorPage-header">
            <Heading level={3}>Error</Heading>
          </div>
          <p>Failed to load device data. Please try again.</p>
          <p>{devicesError.message}</p>
        </div>
      </Page>
    );
  }

  return (
    <Page className="DeviceMonitorPage" titleSegments={pageTitleSegments}>
      <div className="DeviceMonitorPage-content">
        <div className="DeviceMonitorPage-header">
          <Heading level={3}>Device Monitor</Heading>
          <div className="DeviceMonitorPage-headerFilters">
            <div className="DeviceMonitorPage-orgFilter">
              <ComboboxMulti
                items={orgOptions}
                value={selectedOrgIds}
                onChange={handleOrgChange}
                emptyPlaceholder="Filter by Org..."
                label="Org"
              />
            </div>
            <div className="DeviceMonitorPage-barnFilter">
              <ComboboxMulti
                items={barnOptions}
                value={selectedBarnIds}
                onChange={handleBarnChange}
                emptyPlaceholder="Filter by Barn..."
                label="Barn"
              />
            </div>
            <div className="DeviceMonitorPage-statusFilter">
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                itemList={STATUS_OPTIONS}
              />
            </div>
            <div className="DeviceMonitorPage-sortSelect">
              <Select
                label="Sort By"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                itemList={SORT_OPTIONS}
              />
            </div>
          </div>
        </div>

        <div className="DeviceMonitorPage-table">
          <FeedFloTable
            className="DeviceMonitorPage-feedTable"
            headers={tableHeaders}
            rows={tableRows}
            loading={devicesLoading && offset === 0}
            itemTotalCount={allDevices.length}
            maxRows={allDevices.length > 0 ? allDevices.length : PAGE_SIZE}
          />
          {devicesLoading && offset > 0 && (
            <div className="DeviceMonitorPage-loadingMore">Loading more devices...</div>
          )}
          {!devicesLoading && !hasMore && allDevices.length > 0 && (
            <div className="DeviceMonitorPage-endOfList">
              All devices loaded &mdash; {allDevices.length} total
            </div>
          )}
        </div>
      </div>

      <DeviceConnectivityModal
        ref={connectivityModalRef}
        isOpen={!!connectivityModalDevice}
        onClose={() => setConnectivityModalDevice(null)}
        devices={
          connectivityModalDevice
            ? [
              {
                id: connectivityModalDevice.id,
                serial: connectivityModalDevice.serial,
                feedLineName: connectivityModalDevice.feedLineName,
              },
            ]
            : []
        }
        initialDeviceIndex={0}
      />

      <dialog
        ref={chartModalRef}
        className="DeviceMonitorPage-chartModal"
        onClose={() => setChartModalDevice(null)}
      >
        <div className="DeviceMonitorPage-chartModal-header">
          <h3 className="DeviceMonitorPage-chartModal-title">HTTP Chart — {chartModalDevice?.serial}</h3>
          <button
            className="DeviceMonitorPage-chartModal-close"
            onClick={() => setChartModalDevice(null)}
          >
            ✕
          </button>
        </div>
        {chartModalDevice && (
          <TransactionChart
            deviceId={chartModalDevice.id}
            from={dayjs.tz().subtract(2, 'week')}
            to={dayjs.tz()}
          />
        )}
      </dialog>
    </Page>
  );
}

export default DeviceMonitorPage;
